import { Formik } from 'formik';
import { FaCircleChevronDown } from 'react-icons/fa6';
import { Link } from 'react-router-dom';

import Button from '../ui/button';
import AuthForm from './AuthForm';
import AuthInput from './AuthInput';
import AuthSub from './AuthSub';
import AuthSubmit from './AuthSubmit';
import { signInSchema } from './schemas/sign-in.schema';

import { AuthRoutes } from '@/interfaces/auth/AuthRoutes.enum';

type PropTypes = {
	handleSubmit: (username: string, password: string) => Promise<void>;
	loading: boolean;
	isConsumer?: boolean;
};
export default function SignInForm({
	handleSubmit,
	loading,
	isConsumer = false,
}: PropTypes) {
	const initialValues = {
		username: '',
		password: '',
		recaptcha: '',
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={signInSchema}
			onSubmit={({ username, password }) => handleSubmit(username, password)}
		>
			{({ errors, touched }) => (
				<AuthForm>
					<AuthInput
						label="Username"
						name="username"
						type="email"
						placeholder="Email"
						error={!!errors.username}
						touched={touched.username}
						data-test="sign-in-username"
					/>
					<AuthInput
						label="Password"
						name="password"
						type="password"
						placeholder="Password"
						error={!!errors.password}
						touched={touched.password}
						data-test="sign-in-password"
					/>
					<AuthInput
						label="Please verify you are human"
						name="recaptcha"
						type="recaptcha"
						error={!!errors.recaptcha}
						touched={touched.recaptcha}
					/>
					<AuthSubmit
						label="Sign In"
						isLoading={loading}
						data-test="sign-in-submit"
						className="bg-purpleBrand-500 hover:bg-purpleBrand-600 active:bg-purpleBrand-900"
					/>
					<AuthSub>
						<p>
							Forgot your password?{' '}
							<Link
								className="font-medium text-purpleBrand-500"
								to={AuthRoutes.ForgotPassword}
								data-test="link-forgot-password"
							>
								Click here
							</Link>
						</p>

						<p>
							Want to enter account confirmation OTP?{' '}
							<Link
								className="font-medium text-purpleBrand-500"
								to={AuthRoutes.ConfirmUser}
								data-test="link-confirm-user"
							>
								Click here
							</Link>
						</p>
						<div className="flex flex-col items-center justify-center gap-3 pt-5">
							<div className="flex items-center justify-center gap-5">
								<p className="text-sm">New here?</p>
								{isConsumer ? (
									<Link
										to={AuthRoutes.SignUpConsumer}
										className="text-white bg-purpleBrand-500 hover:bg-purpleBrand-600 active:bg-purpleBrand-900 px-5 py-2 rounded-full text-xs font-medium"
									>
										Sign Up
									</Link>
								) : (
									<div className="relative group">
										<Button
											size="mini"
											type="button"
											data-test="sign-up-dropdown"
											transition="none"
											className="text-white bg-purpleBrand-500 hover:bg-purpleBrand-600 active:bg-purpleBrand-900 px-5 inline-block py-0"
										>
											<span className="flex gap-2 items-center justify-center">
												<span className="text-xs font-medium">Sign Up</span>
												<FaCircleChevronDown />
											</span>
										</Button>

										<div className="absolute left-0 hidden w-full rounded-lg bg-white border-l-2 border-r-2 border-b-2 border-gray-300 shadow-lg group-hover:block z-50">
											<ul className="py-2 text-sm text-purpleBrand-500 text-xs">
												<li>
													<Link
														to={AuthRoutes.SignUp}
														className="block px-4 py-2 hover:bg-gray-100"
													>
														As Merchant
													</Link>
												</li>
												<li>
													<Link
														to={AuthRoutes.SignUpConsumer}
														className="block px-4 py-2 hover:bg-gray-100"
													>
														As Customer
													</Link>
												</li>
											</ul>
										</div>
									</div>
								)}
							</div>
							<div className="flex items-center justify-center">
								<p className="text-sm">
									Sign in as{' '}
									<Link
										className="font-medium text-purpleBrand-500"
										to={
											isConsumer ? AuthRoutes.SignIn : AuthRoutes.SignInConsumer
										}
										data-test={
											isConsumer
												? 'link-sign-in-merchant'
												: 'link-sign-in-consumer'
										}
									>
										{isConsumer ? 'Merchant' : 'Customer'}
									</Link>
								</p>
							</div>
						</div>
					</AuthSub>
				</AuthForm>
			)}
		</Formik>
	);
}
